import { CommonModule, formatNumber } from '@angular/common';
import {
  computed,
  Directive,
  effect,
  inject,
  Injector,
  Input,
  output,
  ViewChild,
  OnInit,
} from '@angular/core';
import { LinesEditorComponent } from '../../components';
import {
  GetContextMenuItemsParams,
  MenuItemDef,
  CellValueChangedEvent,
  ColDef,
  CellFocusedEvent,
  RowSelectedEvent,
  EditableCallback,
} from '@ag-grid-community/core';
import {
  DocLineDeletedDto,
  DocLineMovedDto,
  DocLineRole,
  DocLineUpdatedDto,
  StockDocDto,
} from '@digiwall/goliath-documents/proxy';
import { LocalizationModule, LocalizationService } from '@abp/ng.core';
import { EMPTY, Observable } from 'rxjs';
import { DocTextService, StorageLocationService } from '@digiwall/goliath-core/proxy';
import { CurrentCompanyStore, WarehouseStore } from '@digiwall/goliath-core';
import {
  ArticleProductDimension,
  ArticleSelectionLookupDto,
  ArticleSerialService,
} from '@digiwall/goliath-products/proxy';
import { ITypeAheadCellEditorParams, TypeAheadCellEditorComponent } from '@digiwall/ag-grid';
import { AgGridAngular } from '@ag-grid-community/angular';
import { ArticleRangeValueStore } from '@digiwall/goliath-products';
import {
  BaseDocLineEditor,
  BaseDocServiceForLineEditor,
} from '../../components/lines-editor/base-doc-line-editor/base-doc-line-editor';
import {
  IStockDocLineEditor,
  IStockDocService,
  StockDocLineEditorHelperService,
} from '../../components';
import { ObserverQueue } from '@digiwall/core';

export const StockDocLinesImports = [CommonModule, LinesEditorComponent, LocalizationModule];
export const StockDocLinesProviders = [StockDocLineEditorHelperService, ObserverQueue];

@Directive()
export default abstract class StockDocLinesEditorComponent<
    TDoc extends StockDocDto,
    TService extends BaseDocServiceForLineEditor & IStockDocService,
  >
  extends BaseDocLineEditor
  implements IStockDocLineEditor<DocLineUpdatedDto, DocLineMovedDto, DocLineDeletedDto>
{
  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;

  protected _document: TDoc;
  public get document() {
    return this._document;
  }

  public get readonly() {
    return this._readonly;
  }

  public get stockDocService(): TService {
    return this._docService;
  }

  public get updateLines() {
    return this._updateLines;
  }

  public get editorHelper() {
    return this._editorHelper;
  }

  @Input() set stockDoc(value: TDoc) {
    this._document = value;
    this._readonly = this._document.isStockValidated;
  }

  lineUpdatedEvent = output<Partial<DocLineUpdatedDto>>();

  wareHouseStore = inject(WarehouseStore);
  articleRangeValueStore = inject(ArticleRangeValueStore);

  protected abstract get articleLineRole(): DocLineRole;

  descriptionColSpan: Partial<Record<DocLineRole, number>>;

  DocLineRole = DocLineRole;

  protected docTextService = inject(DocTextService);
  protected currentCompanyStore = inject(CurrentCompanyStore);

  addActionBtns = computed(() => {
    let btns = [
      {
        label: this.localizationService.instant('Doc::AddArticleLine'),
        action: () => this.addArticleLine(),
        shortcutKey: null,
      },
    ];
    return btns;
  });

  private lineGridApi = computed(() => this.lineGrid?.grid?.grid?.api);

  constructor(
    protected localizationService: LocalizationService,
    protected _docService: TService,
    protected storageLocationService: StorageLocationService,
    injector: Injector,
    private _editorHelper: StockDocLineEditorHelperService<
      DocLineUpdatedDto,
      DocLineMovedDto,
      DocLineDeletedDto
    >,
    public updateQueue: ObserverQueue,
  ) {
    super(localizationService, _docService, injector, updateQueue);
    this.wareHouseStore.loading();
    _editorHelper.DocLineEditor = this;
    this.colDefs = this.initColDefs();

    effect(() => {
      // Ensure formatting is correct
      if (this.currentCompanyStore.currentCompany() != null && this.lineGridApi() != null) {
        this.lineGridApi().redrawRows();
      }
    });
  }

  protected getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    if (this._readonly) return [];
    return this.contextMenuItems(params);
  };

  protected onCellValueChanged = (
    event: CellValueChangedEvent<any, any>,
  ): void | PromiseLike<void> => {
    let obs: Observable<DocLineUpdatedDto> = this.getCellValueChangedObservable(event);

    this._updateQueue
      .enqueue(() => obs)
      .subscribe({
        next: response => {
          if (response) this._updateLines(response);
        },
        error: _ => {
          let oldData = event.data;
          oldData[event.colDef.field] = event.oldValue;
          this.lineGrid.grid.grid.api.applyTransaction({
            update: [oldData],
          });
        },
      });
  };

  protected colDefs: ColDef<any, any>[];
  protected initColDefs(): ColDef<any, any>[] {
    return [
      this.colDef_displayReference,
      {
        field: 'code',
        pinned: 'left',
        width: 500,
        headerName: this.localizationService.instant('Doc::Reference'),
        editable: params =>
          params.data.lineRole == this.articleLineRole && this.isCellEditable(params),
        cellEditorSelector: ({ data }: { data }) => {
          if (data?.lineRole == this.articleLineRole) {
            return {
              component: TypeAheadCellEditorComponent<TDoc, ArticleSelectionLookupDto>,
              params: {
                lookupFn: (term: string) =>
                  this.articleService.getArticleStockManagedSelectionLookup(term),
                formatFn: item => (typeof item === 'string' ? item : item.label),
                displayLabelFn: item => item.code,
                callbackFn: ({
                  line,
                  selectedItem,
                }: {
                  line: any;
                  selectedItem: ArticleSelectionLookupDto;
                }) => {
                  this._editorHelper.setArticlePropertyByRequest(
                    line.id,
                    selectedItem.id,
                    this._docService.setLineArticle,
                  );
                },
              } as ITypeAheadCellEditorParams<TDoc, ArticleSelectionLookupDto>,
            };
          } else {
            return { component: 'agTextCellEditor' };
          }
        },
      },
      {
        field: 'description',
        width: 400,
        headerName: this.localizationService.instant('::Description'),
        editable: false,
        valueFormatter: row => {
          return row?.data?.description;
        },
      },
      this.colDef_ArticleSerial,
      this._editorHelper.colDef_StockLocation,
      Object.assign({}, this.colDef_Length, {
        editable: params =>
          !params.data.articleUsesSerial &&
          (this.colDef_Length.editable as EditableCallback<any, any>)(params),
      } as Partial<ColDef>),
      Object.assign({}, this.colDef_Width, {
        editable: params =>
          !params.data.articleUsesSerial &&
          (this.colDef_Width.editable as EditableCallback<any, any>)(params),
      } as Partial<ColDef>),
      Object.assign({}, this.colDef_Thickness, {
        editable: params =>
          !params.data.articleUsesSerial &&
          (this.colDef_Thickness.editable as EditableCallback<any, any>)(params),
      } as Partial<ColDef>),
      this.colDef_ArticleRange1,
      Object.assign({}, this.colDef_ArticleRangeValue1, {
        valueFormatter: (p: any) => p.data?.articleRangeValue1 ?? '',
        editable: params =>
          !params.data.articleUsesSerial &&
          (this.colDef_ArticleRangeValue1.editable as EditableCallback<any, any>)(params),
      } as Partial<ColDef>),
      this.colDef_ArticleRange2,
      Object.assign({}, this.colDef_ArticleRangeValue2, {
        valueFormatter: (p: any) => p.data?.articleRangeValue2 ?? '',
        editable: params =>
          !params.data.articleUsesSerial &&
          (this.colDef_ArticleRangeValue2.editable as EditableCallback<any, any>)(params),
      } as Partial<ColDef>),
      Object.assign({}, this.colDef_ArticleLot, {
        editable: params =>
          !params.data.articleUsesSerial &&
          (this.colDef_ArticleLot.editable as EditableCallback<any, any>)(params),
      } as Partial<ColDef>),
      Object.assign({}, this.colDef_Quantity, {
        editable: params =>
          !params.data.articleUsesSerial &&
          (this.colDef_Quantity.editable as EditableCallback<any, any>)(params),
      } as Partial<ColDef>),
      {
        field: 'articleSalesUnitCode',
        headerName: this.localizationService.instant('Doc::StockDoc:Unit'),
        editable: false,
        suppressNavigable: true,
      },
      this.colDef_UnitPrice,
      this.colDef_TotalPrice,
    ];
  }

  protected contextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    let line = params?.node?.data;

    if (this._readonly || !line?.isEditable) return [];

    const selectedRows = params.api.getSelectedNodes()?.map(x => x.data) ?? [];

    const addActions = {
      [this.articleLineRole]: (lineFromId?: string, reference?: number) =>
        this.addArticleLine(lineFromId, reference),
    };

    const addAbove = (lineRole: DocLineRole) => {
      addActions[lineRole]?.(line?.lineFromId, line.reference);
    };

    const addUnder = (lineRole: DocLineRole) => {
      addActions[lineRole]?.(line?.lineFromId, line.reference + 1);
    };

    let menuItems: (string | MenuItemDef)[] = [
      {
        name: this.localizationService.instant('Doc::AddArticle'),
        subMenu: [
          {
            name: this.localizationService.instant('Doc::Above'),
            action: _ => addAbove(this.articleLineRole),
          },
          {
            name: this.localizationService.instant('Doc::Under'),
            action: _ => addUnder(this.articleLineRole),
          },
        ],
      },
    ];

    menuItems.push({
      name: this.localizationService.instant('Doc::Move'),
      subMenu: [this.getMoveAbove(params, line), this.getMoveUnder(params, line)],
    });

    menuItems.push(...this.getDeleteMenuItem(line));

    return menuItems;
  };

  protected getCellValueChangedObservable(event: CellValueChangedEvent<any, any>): Observable<any> {
    let obs: Observable<any>;
    switch (event.column.getColId()) {
      case 'quantity':
        obs = this._docService.setLineQuantity(
          this._document.id,
          this._document.concurrencyStamp,
          event.data.id,
          event.data.quantity,
        );
        break;
      case 'length':
        obs = this._docService.setLineLength(
          this._document.id,
          this._document.concurrencyStamp,
          event.data.id,
          event.data.length,
        );
        break;
      case 'width':
        obs = this._docService.setLineWidth(
          this._document.id,
          this._document.concurrencyStamp,
          event.data.id,
          event.data.width,
        );
        break;
      case 'thickness':
        obs = this._docService.setLineThickness(
          this._document.id,
          this._document.concurrencyStamp,
          event.data.id,
          event.data.thickness,
        );
        break;
      default:
        obs = EMPTY;
        break;
    }
    return obs;
  }

  protected override onCellFocused(grid: AgGridAngular, event: CellFocusedEvent<any, any>): void {
    super.onCellFocused(grid, event);

    const rowIndex = event.rowIndex;

    const articleDocLine: any = grid.api.getDisplayedRowAtIndex(rowIndex);
    if (articleDocLine.data.articleId == null || !articleDocLine.data.computePriceExplanations) {
      this.linePriceDetails = null;
    } else {
      this.linePriceDetails = {
        id: articleDocLine.data.articleId,
        title: `Article : ${articleDocLine.data.code}`,
        details: articleDocLine.data.computePriceExplanations,
      };
    }
  }

  protected addArticleLine(lineFromId?: string, reference?: number) {
    super.addLine(this._docService.addArticleLine, lineFromId, reference);
  }

  public isCellEditable(params) {
    let isEditable = true;
    if (params.colDef.field != 'code') {
      isEditable = params.data.articleId != null;
    }

    return !this._readonly && isEditable;
  }
}
